<template>
    <section
        :id="slice.primary.anchorName || undefined"
        class="slice slice-numbers slice-numbers--key-figures"
        aria-label="numbers-key-figures"
        :data-mui-theme="slice.primary.theme"
        data-testid="slice-numbers-key-figures"
    >
        <div class="slice-content">
            <div class="slice-numbers--key-figures__frame" :class="{'slice-numbers--key-figures__frame--reverse': slice.primary.reverse}">
                <div v-if="slice.primary.title" class="slice-numbers--key-figures__header">
                    <PrismicRichText :field="slice.primary.title" class="slice-title" data-testid="slice-numbers-key-figures-title" />
                </div>
                <div class="slice-numbers--key-figures__items" data-testid="slice-numbers-key-figures-items">
                    <FigureItem
                        v-for="(item, i) in keyFigures"
                        :key="i"
                        :item="item"
                        :index="i"
                        :thousands-separator="thousandsSeparator"
                        :decimal-separator="decimalSeparator"
                        class="slice-numbers--key-figures__item"
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {NumbersSlice} from '@prismicio-types';
    import FigureItem from '../components/FigureItem.vue';

    type NumbersKeyFiguresInZone = Extract<NumbersSlice, {variation: 'numbersKeyFigures'}>;
    const props = defineProps(getSliceComponentProps<NumbersKeyFiguresInZone>());
    const separators = {
        none: '',
        space: ' ',
        comma: ',',
        point: '.',
        quote: "'",
    };
    const thousandsSeparator = separators[props.slice.primary.thousandsSeparator || 'none'];
    const decimalSeparator = separators[props.slice.primary.decimalSeparator || 'point'];

    const keyFigures = (props.slice.primary.figures ?? []).map((item) => ({
        figure: isFilled.number(item.figure) ? item.figure : undefined,
        figurePrefix: isFilled.keyText(item.figurePrefix) ? item.figurePrefix : undefined,
        figureSuffix: isFilled.keyText(item.figureSuffix) ? item.figureSuffix : undefined,
        text: isFilled.keyText(item.text) ? item.text : undefined,
    }));
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-numbers {
        &--key-figures {
            &__frame {
                @include mq.screen_sm(min) {
                    display: flex;
                    justify-content: center;
                    gap: var(--joy-core-spacing-6);

                    &--reverse {
                        flex-direction: row-reverse;
                    }
                }
            }

            &__header {
                text-align: center;
                flex: none;
                margin-bottom: var(--joy-core-spacing-6);

                @include mq.screen_sm(min) {
                    text-align: left;
                    min-width: 300px;
                    width: 30%;
                    margin-bottom: 0;
                }
            }

            &__items {
                display: grid;
                grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: var(--joy-core-spacing-2);
                gap: var(--joy-core-spacing-6) var(--joy-core-spacing-6);

                @include mq.screen_sm(min) {
                    width: calc(100% - 30% - (var(--joy-core-spacing-6) * 2));
                }

                @include mq.screen_lg(min) {
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: var(--joy-core-spacing-9) var(--joy-core-spacing-6);
                }
            }

            .slice-title {
                @include mq.screen_lg(min) {
                    text-align: left;
                }
            }
        }
    }
</style>
